/**
 * SmartSidebar — shared sidebar layout and items for list views.
 * Same theme as SmartTable; sidebar body uses table body color (#fafcfb).
 * Used by: Leads, Bookings, Inventory (configurations), etc.
 */

import React from 'react';
import {
  SIDEBAR_THEME,
  getSidebarTheme,
  getSidebarContainerStyle,
  getSidebarHeaderStyle,
  getSidebarHeaderLabelStyle,
  getSidebarItemStyle,
  getSidebarItemLabelStyle,
  getSidebarItemCountStyle,
  getSidebarDividerStyle,
} from '../../utils/smart_sidebar_helper';

export { SIDEBAR_THEME, getSidebarTheme };

// ─── Sidebar container ─────────────────────────────────────────────────────
export function SmartSidebar({ children, theme, width = 220, bodyBg = true, style = {} }) {
  const containerStyle = getSidebarContainerStyle(theme, { width, bodyBg });
  return (
    <div style={{ ...containerStyle, ...style }}>
      {children}
    </div>
  );
}

// ─── Section header (e.g. "Pipeline", "CONFIGURATIONS") ──────────────────────
export function SmartSidebarHeader({ children, theme, style = {} }) {
  const resolvedTheme = getSidebarTheme(theme);
  const headerStyle = getSidebarHeaderStyle(theme);
  const labelStyle = getSidebarHeaderLabelStyle(theme);
  return (
    <div style={headerStyle}>
      <div style={{ ...labelStyle, ...style }}>{children}</div>
    </div>
  );
}

// ─── Clickable sidebar item (optional count badge) ───────────────────────────
export function SmartSidebarItem({ label, count, active, onClick, theme, leftContent, style = {} }) {
  const resolvedTheme = getSidebarTheme(theme);
  const itemStyle = getSidebarItemStyle(theme, { active });
  const labelStyle = getSidebarItemLabelStyle(theme, { active });
  const countStyle = getSidebarItemCountStyle(theme, { active });

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && typeof onClick === 'function') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      role={typeof onClick === 'function' ? 'button' : undefined}
      tabIndex={typeof onClick === 'function' ? 0 : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      style={{ ...itemStyle, ...style }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, flex: 1 }}>
        {leftContent}
        <span style={labelStyle}>{label}</span>
      </div>
      {count != null && (
        <span style={countStyle}>{count}</span>
      )}
    </div>
  );
}

// ─── Divider line between sections ──────────────────────────────────────────
export function SmartSidebarDivider({ theme, style = {} }) {
  const dividerStyle = getSidebarDividerStyle(theme);
  return <div style={{ ...dividerStyle, ...style }} />;
}

export default {
  SmartSidebar,
  SmartSidebarHeader,
  SmartSidebarItem,
  SmartSidebarDivider,
  SIDEBAR_THEME,
  getSidebarTheme,
};
